// ─────────────────────────────────────────────────────────────────────────────
// Bridges the typed `AnswerValue` discriminated union (used by the Responder
// primitives) and the legacy JSON-string answer payload stored by the
// backend. The wire shape varies per `answerType` — see the responder
// audit for the full table. These helpers are pure and synchronous.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  BuilderAnswerOption,
  BuilderAnswerType,
  DataPointColumn,
  RawAnswerRequest,
  RawAnswerRequestQuestion,
  ResponseReviewStatus,
} from '@/api/questionnaires/types';
import { isSingleRowDatapoint } from '../-constants/specializedDatapoints';
import {
  type AnswerRequest,
  type AnswerRequestQuestion,
  type AnswerRequestStatistics,
  type AnswerValue,
  BUILDER_ANSWER_TYPES,
  defaultAnswerValue,
  type ResponderTableRow,
  type TableCellValue,
} from '../-types';
import { normalizeBuilderDetail } from './builderSerializers';

let parsedRowSeed = 0;
function nextParsedRowKey(): string {
  parsedRowSeed += 1;
  return `parsed-row-${parsedRowSeed}`;
}

/** Parse the saved-answer JSON string into a typed `AnswerValue`. Returns
 *  `undefined` when the answer is empty or unparseable — callers treat
 *  that as "responder hasn't touched the field yet".
 *
 *  `tableColumns` is only needed for TABLE answers — used to reverse
 *  the camelCase + flattened-compound encoding the serializer produces
 *  (wire stores `{totalAnnualConsumption: 34, unit: 'KG'}`; the table
 *  cell editor expects `{total_annual_consumption: {value: 34, unit:
 *  'KG', year: null}}`). */
export function parseAnswer(
  raw: string | null,
  answerType: BuilderAnswerType | string,
  options?: BuilderAnswerOption[],
  tableColumns?: DataPointColumn[],
): AnswerValue | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  switch (answerType) {
    case BUILDER_ANSWER_TYPES.SHORT_TEXT:
    case BUILDER_ANSWER_TYPES.LONG_TEXT: {
      // Legacy backend sometimes stores the raw string (e.g. when the
      // responder marked the question as "Not available", the answer is
      // a JSON-encoded empty string `""`). Other paths store the
      // `{ value: "text" }` envelope. Handle both.
      if (typeof parsed === 'string') return { kind: 'text', value: parsed };
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'string') return { kind: 'text', value: v };
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.NUMBER: {
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'number') return { kind: 'number', value: v };
      if (typeof v === 'string' && v.length > 0) {
        const n = Number(v);
        return { kind: 'number', value: Number.isNaN(n) ? null : n };
      }
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.NUMBER_UOM: {
      const obj = parsed as {
        value?: unknown;
        unit?: unknown;
        year?: unknown;
      } | null;
      if (!obj || typeof obj !== 'object') return undefined;
      const value =
        typeof obj.value === 'number'
          ? obj.value
          : typeof obj.value === 'string' && obj.value.length > 0
            ? Number(obj.value)
            : null;
      const unit = typeof obj.unit === 'string' ? obj.unit : null;
      const year =
        typeof obj.year === 'number'
          ? obj.year
          : typeof obj.year === 'string' && obj.year.length > 0
            ? Number(obj.year)
            : null;
      return {
        kind: 'numberUom',
        value: { value, unit, year },
      };
    }

    case BUILDER_ANSWER_TYPES.DATE: {
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'string') return { kind: 'date', value: v };
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.YES_OR_NO: {
      // Legacy option-array shape: [{ id, answer: boolean, value: null }].
      if (Array.isArray(parsed)) {
        const item = parsed[0] as
          | { answer?: unknown; id?: unknown }
          | undefined;
        if (item && typeof item.answer === 'boolean') {
          return { kind: 'select', value: item.answer ? 'yes' : 'no' };
        }
      }
      // Canonical shapes: YES_NO → { value: 'YES' | 'NO' } (case-insensitive),
      // BOOLEAN → { value: true | false }.
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'boolean') {
        return { kind: 'select', value: v ? 'yes' : 'no' };
      }
      if (typeof v === 'string') {
        const lc = v.toLowerCase();
        if (lc === 'yes' || lc === 'true')
          return { kind: 'select', value: 'yes' };
        if (lc === 'no' || lc === 'false')
          return { kind: 'select', value: 'no' };
      }
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.SELECT: {
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'string') return { kind: 'select', value: v };
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.CHECK_BOXES: {
      const v = (parsed as { value?: unknown } | null)?.value;
      if (Array.isArray(v)) {
        const validKeySet =
          options && options.length > 0
            ? new Set(
                options
                  .map(o => o.key ?? o.id)
                  .filter((k): k is string => typeof k === 'string'),
              )
            : null;
        const keys = v
          .map(item => {
            if (item && typeof item === 'object') {
              const obj = item as { key?: unknown; id?: unknown };
              const k = obj.key ?? obj.id;
              return typeof k === 'string' ? k : null;
            }
            if (typeof item === 'string') return item;
            return null;
          })
          .filter((k): k is string => k !== null)
          .filter(k =>
            validKeySet && validKeySet.size > 0 ? validKeySet.has(k) : true,
          );
        return { kind: 'multi', value: keys };
      }
      return undefined;
    }

    case BUILDER_ANSWER_TYPES.TABLE: {
      const v = (parsed as { value?: unknown } | null)?.value;
      // Single-row datapoints (carbon intensity, GHG offset) store `value`
      // as the single row object, not an array — the serializer unwraps
      // them so their datapoint validator accepts the answer. Re-wrap it
      // into a one-row array so the table field can render it back.
      const rowsInput = Array.isArray(v)
        ? v
        : v && typeof v === 'object'
          ? [v]
          : null;
      if (!rowsInput) return undefined;
      const rows: ResponderTableRow[] = rowsInput.map(row => {
        if (!row || typeof row !== 'object') {
          return { key: nextParsedRowKey(), values: {} };
        }
        return {
          key: nextParsedRowKey(),
          values: unflattenTableRow(
            row as Record<string, unknown>,
            tableColumns,
          ),
        };
      });
      return { kind: 'table', value: rows };
    }

    case BUILDER_ANSWER_TYPES.PHOTO_UPLOAD: {
      // Wire stores `{ value: [{ id, url, fileType }, ...] }`. Legacy rows
      // sometimes JSON-encode the array again inside `value`, so unwrap both.
      let v: unknown = (parsed as { value?: unknown } | null)?.value ?? parsed;
      if (typeof v === 'string') {
        try {
          v = JSON.parse(v);
        } catch {
          return undefined;
        }
      }
      if (!Array.isArray(v)) return undefined;
      const images = v.flatMap(item => {
        if (!item || typeof item !== 'object') return [];
        const { id, url, fileType } = item as Record<string, unknown>;
        if (typeof id !== 'string' || typeof url !== 'string') return [];
        return [
          {
            id,
            url,
            fileType: typeof fileType === 'string' ? fileType : 'image/*',
          },
        ];
      });
      return { kind: 'images', value: images };
    }

    case BUILDER_ANSWER_TYPES.VIDEO: {
      // Wire stores a single `{ value: { id, url, fileType } }` (like the
      // company-profile video datapoint). Legacy rows may JSON-encode the
      // object again inside `value`, so unwrap both.
      let v: unknown = (parsed as { value?: unknown } | null)?.value ?? parsed;
      if (typeof v === 'string') {
        try {
          v = JSON.parse(v);
        } catch {
          return undefined;
        }
      }
      if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
      const { id, url, fileType } = v as Record<string, unknown>;
      if (typeof url !== 'string') return undefined;
      return {
        kind: 'video',
        value: {
          id: typeof id === 'string' ? id : '',
          url,
          fileType: typeof fileType === 'string' ? fileType : 'video/mp4',
        },
      };
    }

    case BUILDER_ANSWER_TYPES.DOCUMENTS:
    case BUILDER_ANSWER_TYPES.AUDIT:
    case BUILDER_ANSWER_TYPES.CERTIFICATE: {
      const v = (parsed as { value?: unknown } | null)?.value;

      if (!Array.isArray(v)) return undefined;

      // Map each `{ optionId, id }` back to a slot by its option index.
      // Slot count derives from the question's `options` length.
      const ids = (v ?? [])
        .map(item => {
          if (!item || typeof item !== 'object') return null;
          const id = (item as any).id;
          return typeof id === 'string' ? id : null;
        })
        .filter((id): id is string => id !== null);

      return {
        kind: 'assetLinks',
        value: ids,
      };
    }

    default: {
      const v = (parsed as { value?: unknown } | null)?.value;
      if (typeof v === 'string') return { kind: 'text', value: v };
      return undefined;
    }
  }
}

/** Wire shape for a serialized answer. The backend stores it under
 *  `RESPONSE.ANSWER` as a JSON column, so we hand it the object — fetch
 *  stringifies the body wrapper exactly once. Encoding the answer
 *  ourselves caused double-stringification (`answer: "\"value\":..."`)
 *  which made every datapoint validator reject the answer. */
export type SerializedAnswer =
  | { value: unknown }
  | { value: unknown; unit?: string; year?: number }
  | Array<unknown>;

/** Serialize a typed `AnswerValue` into the JSON-compatible object the
 *  backend expects. Returns `null` when the value is effectively empty
 *  so the responder can save a cleared field. */
export function serializeAnswer(
  value: AnswerValue | undefined,
  answerType: BuilderAnswerType | string,
  options?: BuilderAnswerOption[],
  /** Original server `fieldTypeId`. YES_NO and BOOLEAN both collapse onto the
   *  YES_OR_NO answer type but need different payloads (`'YES'`/`'NO'` string
   *  vs `true`/`false` boolean) to satisfy the datapoint schema. */
  rawAnswerType?: string,
  /** The question's bound data point (`retracedId`). Single-row TABLE
   *  datapoints (carbon intensity, GHG offset) expect `value` to be the
   *  single row object, not an array of rows. */
  retracedDatapointId?: string,
): SerializedAnswer | null {
  if (!value) return null;

  switch (answerType) {
    case BUILDER_ANSWER_TYPES.SHORT_TEXT:
    case BUILDER_ANSWER_TYPES.LONG_TEXT: {
      const v = value.kind === 'text' ? value.value : '';
      if (v.trim().length === 0) return null;
      return { value: v };
    }

    case BUILDER_ANSWER_TYPES.NUMBER: {
      const v = value.kind === 'number' ? value.value : null;
      if (v === null) return null;
      return { value: v };
    }

    case BUILDER_ANSWER_TYPES.NUMBER_UOM: {
      const v =
        value.kind === 'numberUom'
          ? value.value
          : { value: null, unit: null, year: null };
      if (v.value === null) return null;
      const body: { value: number; unit?: string; year?: number } = {
        value: v.value,
      };
      if (v.unit) body.unit = v.unit;
      if (v.year !== null && v.year !== undefined) body.year = v.year;
      return body;
    }

    case BUILDER_ANSWER_TYPES.DATE: {
      const v = value.kind === 'date' ? value.value : '';
      if (!v) return null;
      return { value: v };
    }

    case BUILDER_ANSWER_TYPES.YES_OR_NO: {
      const v = value.kind === 'select' ? value.value : null;
      if (v !== 'yes' && v !== 'no') return null;
      // The datapoint schema decides the shape: BOOLEAN wants a boolean
      // `value`, YES_NO wants the `'YES'`/`'NO'` string enum. Mirrors the old
      // frontend's YesOrNo answer, which sends `{ value: 'YES' | 'NO' }`
      // (or `{ value: true | false }` in boolean mode).
      const isBoolean =
        rawAnswerType === 'BOOLEAN' ||
        rawAnswerType === 'TOGGLE' ||
        rawAnswerType === 'CHECK_BOX';
      return {
        value: isBoolean ? v === 'yes' : v === 'yes' ? 'YES' : 'NO',
      };
    }

    case BUILDER_ANSWER_TYPES.SELECT: {
      const v = value.kind === 'select' ? value.value : null;
      if (v === null) return null;
      return { value: v };
    }

    case BUILDER_ANSWER_TYPES.CHECK_BOXES: {
      const v = value.kind === 'multi' ? value.value : [];
      if (v.length === 0) return null;
      const optionKeySet =
        options && options.length > 0
          ? new Set(
              options
                .map(o => o.key ?? o.id)
                .filter((k): k is string => typeof k === 'string'),
            )
          : null;
      const filtered =
        optionKeySet && optionKeySet.size > 0
          ? v.filter(k => optionKeySet.has(k))
          : v;
      if (filtered.length === 0) return null;
      // Multi-select datapoints (relationship_type, facility_processes,
      // product_types, product_levels, …) store answers as a bare
      // `[{ id }]` array — the canonical relational-datapoint shape the old
      // frontend and the company profile read back. Only true checkbox /
      // multiple-choice questions use the `{ id, key, isChecked, followUp }`
      // shape (they carry per-option follow-ups). Emitting the checkbox
      // shape for a multi-select made the old frontend render "undefined"
      // for every option, since it doesn't understand those extra fields.
      const isMultiSelect =
        rawAnswerType === 'MULTI_SELECT' || rawAnswerType === 'MULTIPLE_SELECT';
      if (isMultiSelect) {
        return { value: filtered.map(k => ({ id: k })) };
      }
      return {
        value: filtered.map(k => ({
          id: k,
          key: k,
          isChecked: true,
          followUp: null,
        })),
      };
    }

    case BUILDER_ANSWER_TYPES.TABLE: {
      const rows = value.kind === 'table' ? value.value : [];
      if (rows.length === 0) return null;
      // Strip the client-side `key` and flatten any compound
      // `{value, unit, year}` cells (NUMBER_DYNAMIC_UOM / _WITH_YEAR
      // editors) into the column key + sibling `unit` / `year` fields.
      // The datapoint validator on environmental tables (energy
      // consumption, water sources, etc.) rejects nested compound
      // cells — it expects the unit and year as their own row-level
      // entries (matches the legacy responder's per-datapoint forms).
      const flatRows = rows.map(r => flattenTableRow(r.values));
      // Single-row datapoints (carbon intensity, GHG offset) expose their
      // fields as a single row, and their datapoint validator expects
      // `value` to be that row object — not an array. Unwrap it so the
      // answer validates (matches the legacy per-datapoint forms, which
      // sent a plain object).
      if (isSingleRowDatapoint(retracedDatapointId)) {
        return { value: flatRows[0] };
      }
      return { value: flatRows };
    }

    case BUILDER_ANSWER_TYPES.PHOTO_UPLOAD: {
      const images = value.kind === 'images' ? value.value : [];
      if (images.length === 0) return null;
      return { value: images };
    }

    case BUILDER_ANSWER_TYPES.VIDEO: {
      // A single uploaded video stored as `{ value: { id, url, fileType } }`.
      const video = value.kind === 'video' ? value.value : null;
      if (!video) return null;
      return { value: video };
    }

    case BUILDER_ANSWER_TYPES.DOCUMENTS:
    case BUILDER_ANSWER_TYPES.AUDIT:
    case BUILDER_ANSWER_TYPES.CERTIFICATE: {
      const slots = value.kind === 'assetLinks' ? value.value : [];
      const items: Array<{ optionId: string; id: string }> = [];
      slots.forEach((id, i) => {
        if (id === null) return;
        const option = options?.[i];
        const optionId = option?.id ?? option?.key ?? `slot-${i}`;
        items.push({ optionId, id });
      });
      if (items.length === 0) return null;
      return { value: items };
    }

    default: {
      // Unknown answer types — fall back to a text payload so the response
      // round-trips.
      const v = value.kind === 'text' ? value.value : JSON.stringify(value);
      if (!v) return null;
      return { value: v };
    }
  }
}

/** Build the typed answer-map for a loaded request. Each question's
 *  `answer` string is parsed once and stored under its question id. */
export function buildAnswerMap(
  questions: ReadonlyArray<{
    questionId: string;
    answerType: BuilderAnswerType | string;
    options?: BuilderAnswerOption[];
    tableColumns?: DataPointColumn[];
    answer: string | null;
  }>,
): Map<string, AnswerValue> {
  const map = new Map<string, AnswerValue>();
  for (const q of questions) {
    const parsed = parseAnswer(
      q.answer,
      q.answerType,
      q.options,
      q.tableColumns,
    );
    if (parsed) map.set(q.questionId, parsed);
  }
  return map;
}

// Re-export for callers that need to seed an empty value.
export { defaultAnswerValue };

/** Normalises the raw `GET /v1/questionnaires/requests/:id` response into
 *  an `AnswerRequest`. Sections + questions go through the builder
 *  normaliser (which already handles the legacy `title` / `id` field
 *  names); responder-specific per-question fields (`answer`,
 *  `reviewStatus`, `isNotAvailable`) are layered on by id.
 *
 *  `requestId` is the URL param — the response's top-level `id` is the
 *  *questionnaire* id, not the request id, so the caller must pass the
 *  request id explicitly. */
export function normalizeAnswerRequest(
  raw: RawAnswerRequest,
  requestId: string,
): AnswerRequest {
  const builder = normalizeBuilderDetail(raw);

  const rawById = new Map<string, RawAnswerRequestQuestion>();
  for (const q of raw.questions ?? []) {
    const id = q.id ?? q.questionId;
    if (id) rawById.set(id, q);
  }

  // Once the request moves to IN_REVIEW, the server doesn't always
  // bump each per-answer status — it can stay `NOT_SUBMITTED` even
  // though the responder already submitted. Surface answers in that
  // window as "In review" so the per-question badge matches reality
  // (legacy parity — the old StatusBar did the same client-side
  // promotion).
  const requestPromotesToInReview = raw.requestStatus === 'IN_REVIEW';

  // Dedupe by questionId. The responses endpoint LEFT JOINs each question
  // to `companies_service_templates` on the datapoint id, so a datapoint
  // that belongs to more than one template (e.g. `waste_water_treatment_
  // proficiency` → WASTEWATER_MANAGEMENT + WASTE_WATER_MANAGEMENT) fans the
  // question out into multiple rows with the same id. Without deduping, the
  // responder renders that question twice and — because answer state is
  // keyed by questionId — filling one row mirrors into the other. Keep the
  // first occurrence (its `retracedTemplateId` is enough to sync).
  const seenQuestionIds = new Set<string>();
  const questions: AnswerRequestQuestion[] = builder.questions
    .filter(bq => {
      if (seenQuestionIds.has(bq.questionId)) return false;
      seenQuestionIds.add(bq.questionId);
      return true;
    })
    .map(bq => {
      const rawQ = rawById.get(bq.questionId);
      // Wire uses `status`; older paths used `reviewStatus`. Take either.
      const wireStatus = rawQ?.status ?? rawQ?.reviewStatus ?? 'NOT_SUBMITTED';
      const hasAnswer =
        rawQ?.answer !== null &&
        rawQ?.answer !== undefined &&
        rawQ?.answer !== '';
      const effectiveStatus: ResponseReviewStatus =
        requestPromotesToInReview && wireStatus === 'NOT_SUBMITTED' && hasAnswer
          ? 'IN_REVIEW'
          : wireStatus;
      return {
        ...bq,
        ...(typeof rawQ?.commentCount === 'number'
          ? { commentCount: rawQ.commentCount }
          : {}),
        answer: rawQ?.answer ?? null,
        reviewStatus: effectiveStatus,
        isNotAvailable: rawQ?.isNotAvailable === 1 ? 1 : 0,
      };
    });

  // Cast once; the wire shape is the legacy stats blob (see the
  // responder audit). Each field defaults to 0 so the summary card
  // never has to guard against undefined.
  const rawStats = raw.statistics as
    | Partial<AnswerRequestStatistics>
    | undefined;

  return {
    id: requestId,
    questionnaireId: raw.questionnaireId ?? raw.id ?? '',
    name: builder.name,
    description: builder.description,
    requestStatus: raw.requestStatus ?? 'OPEN',
    forCompanyId: raw.forCompanyId ?? raw.companyId ?? '',
    onBehalfOfCompanyId:
      raw.onBehalfOfCompanyId ??
      raw.onBehalfOfCompany?.id ??
      raw.companyId ??
      '',
    companyId: raw.companyId ?? raw.forCompanyId ?? '',
    // The toggle on each global / datapoint-bound question reads:
    // "Update value on {{companyName}} profile". On the wire the
    // on-behalf-of company's name lives on the nested
    // `onBehalfOfCompany` object — fall through the legacy flat
    // `companyName` for older payloads.
    companyName: raw.onBehalfOfCompany?.name ?? raw.companyName ?? '',
    requestCompanyId: raw.requestCompany?.id ?? '',
    requestCompanyName: raw.requestCompany?.name ?? '',
    sections: builder.sections,
    questions,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    updatedByUserName: raw.updatedByUserName ?? null,
    ...(raw.dueDate ? { dueDate: raw.dueDate } : {}),
    ...(rawStats
      ? {
          statistics: {
            totalQuestions: rawStats.totalQuestions ?? 0,
            totalResponses: rawStats.totalResponses ?? 0,
            totalApprovedResponses: rawStats.totalApprovedResponses ?? 0,
            totalInReviewResponses: rawStats.totalInReviewResponses ?? 0,
            totalDeniedResponses: rawStats.totalDeniedResponses ?? 0,
            totalMissingResponses: rawStats.totalMissingResponses ?? 0,
            approvedResponsesPercentage:
              rawStats.approvedResponsesPercentage ?? 0,
            inReviewResponsesPercentage:
              rawStats.inReviewResponsesPercentage ?? 0,
            deniedResponsesPercentage: rawStats.deniedResponsesPercentage ?? 0,
            missingResponsesPercentage:
              rawStats.missingResponsesPercentage ?? 0,
            ...(rawStats.totalComments !== undefined
              ? { totalComments: rawStats.totalComments }
              : {}),
            ...(rawStats.responsesPercentage !== undefined
              ? { responsesPercentage: rawStats.responsesPercentage }
              : {}),
          },
        }
      : {}),
  };
}

/** Flattens compound `{value, unit, year}` cells (the wire shape our
 *  `NUMBER_DYNAMIC_UOM` cell editor produces) into the row's column key
 *  + sibling `unit` and `year` fields. Plain cells pass through.
 *
 *  Environmental datapoints (energy consumption, water sources, GHG,
 *  etc.) reject nested compound cells — their backend validators
 *  expect `unit` / `year` as their own row-level fields, matching the
 *  legacy responder's per-datapoint forms (`ConsumptionCategoriesAnswerForm`
 *  serialised separate `unit`, `totalAnnualConsumption`, `year` fields).
 *
 *  If the row already has an explicit `unit` or `year` column, that
 *  value wins — we never overwrite a sibling that the user explicitly
 *  set. */
function flattenTableRow(
  values: Record<string, TableCellValue>,
): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const [colId, cellValue] of Object.entries(values)) {
    // Column ids on the wire are snake_case (`consumption_category`,
    // `total_annual_consumption`, etc.) but the datapoint validator
    // expects camelCase row keys — matches the legacy responder's
    // per-datapoint forms (`consumptionCategory`, `totalAnnualConsumption`).
    const key = snakeToCamel(colId);
    if (cellValue && typeof cellValue === 'object' && 'value' in cellValue) {
      const compound = cellValue as {
        value: number | null;
        unit: string | null;
        year: string | null;
      };
      flat[key] = compound.value;
      if (
        compound.unit !== null &&
        compound.unit !== undefined &&
        !('unit' in flat)
      ) {
        flat.unit = compound.unit;
      }
      if (
        compound.year !== null &&
        compound.year !== undefined &&
        !('year' in flat)
      ) {
        flat.year = coerceYear(compound.year);
      }
    } else {
      flat[key] = coerceCell(colId, cellValue);
    }
  }
  return flat;
}

function snakeToCamel(s: string): string {
  return s.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

/** SELECT_YEAR cells store the year as a string (matching the option
 *  key, e.g. `"2023"`). Datapoint validators expect numeric years
 *  (legacy stored `Date.getFullYear()`). Coerce here so every year
 *  field hits the wire as a number. */
function coerceCell(colId: string, value: TableCellValue): unknown {
  if (
    typeof value === 'string' &&
    (colId === 'year' || colId.endsWith('Year'))
  ) {
    return coerceYear(value);
  }
  return value;
}

function coerceYear(value: string | number | null): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (!/^\d{4}$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** Reverse of `flattenTableRow` — takes a wire row (camelCase keys with
 *  flattened compound cells) and produces the internal row shape (column
 *  ids matching `meta.columns`, compound `{value, unit, year}` cells
 *  reconstructed for NUMBER_DYNAMIC_UOM / _CURRENCY columns).
 *
 *  Without this, a row that came back from the server reads as empty
 *  cells because the keys (camelCase) don't match the snake_case column
 *  ids the editor uses for lookups. */
function unflattenTableRow(
  wireRow: Record<string, unknown>,
  columns: DataPointColumn[] | undefined,
): Record<string, TableCellValue> {
  // 1. camelCase → snake_case for every key, so lookups against
  //    `meta.columns[].id` succeed.
  const snakeRow: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(wireRow)) {
    snakeRow[camelToSnake(key)] = val;
  }

  // 2. Rebuild compound cells for NUMBER_DYNAMIC_* columns. Without
  //    column metadata we leave the row flat (renderer falls back to
  //    treating the value as a plain number).
  if (columns) {
    for (const col of columns) {
      if (isCompoundColumnType(col.type)) {
        const flat = snakeRow[col.id];
        if (typeof flat === 'number') {
          const unitSibling =
            typeof snakeRow.unit === 'string'
              ? snakeRow.unit
              : // Single-unit datapoints (carbon intensity, GHG offset)
                // share one row-level `unit` across several compound
                // columns, and it's consumed by the first column below.
                // Fall back to the column's own `unit` default so the
                // remaining columns still render their unit.
                typeof col.unit === 'string'
                ? col.unit
                : null;
          const yearSibling = isYearAwareCompound(col.type)
            ? coerceYearString(snakeRow.year)
            : null;
          snakeRow[col.id] = {
            value: flat,
            unit: unitSibling,
            year: yearSibling,
          };
          // The `unit` sibling has been absorbed — remove so it doesn't
          // round-trip as a duplicate row field on the next save.
          delete snakeRow.unit;
        }
      }
      if (col.type === 'SELECT_YEAR') {
        // Wire returns year as a number; the SELECT_YEAR editor stores
        // it as the option key (string).
        const v = snakeRow[col.id];
        if (typeof v === 'number') {
          snakeRow[col.id] = String(v);
        }
      }
    }
  }

  return snakeRow as Record<string, TableCellValue>;
}

function camelToSnake(s: string): string {
  return s.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function isCompoundColumnType(type: string): boolean {
  return (
    type === 'NUMBER_DYNAMIC_UOM' ||
    type === 'NUMBER_DYNAMIC_UOM_WITH_YEAR' ||
    type === 'NUMBER_DYNAMIC_CURRENCY' ||
    type === 'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR' ||
    type === 'NUMBER_UOM'
  );
}

function isYearAwareCompound(type: string): boolean {
  return (
    type === 'NUMBER_DYNAMIC_UOM_WITH_YEAR' ||
    type === 'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR'
  );
}

function coerceYearString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}
